'use client';

/**
 * AI SDK v6 based chat hook for CRUCIBLE
 *
 * Implements the Vercel AI SDK useChat hook with custom transport
 * to pass sessionId and handle CRUCIBLE-specific content routing.
 *
 * Tool-based content routing:
 * - Uses typed tool definitions from crucible-tools.ts
 * - Extracts tool invocations from AI responses
 * - Routes content to appropriate tabs (XML, JSON, Canvas, Forms)
 */
import { useChat as useAIChat } from '@ai-sdk/react';
import { DefaultChatTransport, isReasoningUIPart } from 'ai';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from '@/lib/toast';
import { generateStudyShell, ShellGenerationError } from '@/lib/parallel-build/generate-shell';
import { Message, MessagePart, ExecutedTool } from '@/types/chat';
import type { CrucibleToolStatusData } from '@/types/crucible-tool-status';
import { buildPersistableTools, buildToolsUsedEntry, toExecutedToolStatus } from './use-chat-tool-persistence';
import { apiClient } from '@/lib/api-client';
import { encodePayloadBase64 as encodePayloadB64, wrapPayload } from '@/lib/payload-encode';
import { interpretAutoTitleResponse, shouldAutoTitle } from '@/lib/auto-title';
import { ContentTabType } from './use-content-tabs';
import { UploadedFile } from '@/types/chat';
import { extractToolResults, executeTool, parseToolResult, type ToolHandler, type ToolResult } from './crucible-tools';
import { trackChatError, trackEvent, trackToolExecution } from '@/lib/telemetry';
import { canvasReferenceMarkdown, canvasLinkForTool, targetToolForMode, type ChatMode, type ToolId } from '@/lib/ai/prompts';
import { canvasOwnerFor } from '@/lib/ai/canvas-ownership';
import { isHandoffTarget } from '@/lib/workspaces/registry';
import { TurnTracker } from '@/lib/ai/turn-tracker';
import { withTurnMode } from '@/lib/ai/turn-mode';
import { EMPTY_TURN_NOTICE, UNFINISHED_TURN_NOTICE, shouldShowNoticeFromParts } from '@/lib/ai/assistant-text';
import { chatApiErrorNotice } from '@/lib/ai/api-error-notice';
import { isUserVisibleTool, visibleTools } from '@/lib/ai/tool-visibility';
import { deriveTurnActivity, type TurnActivity } from '@/lib/ai/turn-activity';
import { resolveContextInjectionMode } from '@/lib/ai/context-injection';
import { finalizeTurnParts, stripInternalBlocks } from '@/lib/ai/turn-parts';
import { claimForRecording } from '@/lib/ai/tool-call-ledger';
import { buildRestoredMaps } from '@/lib/ai/restore-message-maps';
import { turnStepsFromParts, restoredTurnParts } from '@/lib/ai/turn-reasoning';
import { StreamWatchdog } from '@/lib/ai/stream-watchdog';
import { getActiveStudyContextForRegistration } from '@/lib/study-thread-bindings';
interface UseCrucibleChatOptions {
  sessionId: string;
  isConnected: boolean;
  uploadedFiles: UploadedFile[];
  addOrUpdateContentTab: (type: ContentTabType, content: string, title?: string, isFragment?: boolean, messageId?: string, targetTool?: ToolId) => void;
  addComparatorTab: (leftXml: string, rightXml: string, leftLabel?: string, rightLabel?: string, title?: string) => void;
  mode?: ChatMode;
  activePage?: string;
  /** Latest Compli assessment summary — sent with each turn so the BFF can
   *  inject it into conversation history. */
  assessmentContext?: string;
  onQualioTool?: () => void;
  /** Workspace-level tracker — survives session switches for list indicators. */
  reportStreamingSession?: (sessionId: string, active: boolean) => void;
}

// Insert a paragraph break where the AI SDK concatenated two text segments
// across a tool-use boundary without whitespace (e.g. `simultaneously.Now let me`).
// The model often emits a sentence-ending punctuation right before a tool call
// and resumes mid-thought after — when both halves land in the same text part,
// the boundary reads as a missing space. We detect end-punct directly followed
// by a capital letter + lowercase (a new word) and split with `\n\n` so markdown
// renders a paragraph break.
function normalizeSentenceBoundaries(text: string): string {
  if (!text) return text;
  return text.replace(/([.!?:])([A-Z][a-z])/g, '$1\n\n$2');
}

/**
 * One string field off a payload whose type the caller never declared.
 *
 * Returns undefined for a missing key AND for a non-string value, so a
 * numeric or object field can never reach a slot typed `string`.
 */
function stringField(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const found = (value as Record<string, unknown>)[key];
  return typeof found === 'string' ? found : undefined;
}

/**
 * The two fields read off an AI SDK message part here. Declared structurally
 * rather than as the SDK's `UIMessagePart` union because the stream also
 * carries this app's `data-crucible-*` parts, which are not members of it —
 * the same reason `message-steps.ts` narrows to its own `RawPart`.
 */
interface TextBearingPart {
  type?: string;
  text?: string;
}

// Extract text from AI SDK message parts
function getTextFromParts(parts: readonly TextBearingPart[] | undefined): string {
  if (!parts || !Array.isArray(parts)) return '';
  const joined = parts.filter(p => p.type === 'text').map(p => p.text ?? '').join('\n\n');
  return normalizeSentenceBoundaries(joined);
}
export function useChat({
  sessionId,
  isConnected,
  uploadedFiles,
  addOrUpdateContentTab,
  addComparatorTab,
  mode = 'study-design',
  activePage,
  assessmentContext,
  reportStreamingSession
}: UseCrucibleChatOptions) {
  // Delegates to the shared client-safe encoder (lib/payload-encode.ts), which
  // the parallel-build routes now use too. Kept as a useCallback with empty
  // deps so the transport useMemo below keeps its existing stable identity
  // rather than changing dependency semantics as a side effect of the dedupe.
  const encodePayloadBase64 = useCallback((payload: unknown): string => encodePayloadB64(payload), []);
  const [modelInfo, setModelInfo] = useState<{
    deployment: string;
  } | null>(null);
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  // Map of messageId -> executed tools (for display in chat UI)
  const [messageToolsMap, setMessageToolsMap] = useState<Record<string, ExecutedTool[]>>({});
  // `elapsed_ms` and `tool_stats.iterations` are declared because both paths
  // that fill this map write them — onFinish spreads `{ ...streamData,
  // elapsed_ms }`, and the reload path spreads `RestoredStats`, which has
  // carried both for as long as it has existed. Neither was in this type, so
  // the stats footer's own input looked as if it could never have a duration,
  // and every consumer had to assert past it to read one.
  const [messageStatsMap, setMessageStatsMap] = useState<Record<string, {
    usage?: Record<string, number>;
    tool_stats?: {
      count: number;
      tools: string[];
      iterations?: number;
    };
    elapsed_ms?: number;
  }>>({});
  // Map of messageId -> seconds spent with a reasoning part actually
  // streaming, summed across every step of the turn. Feeds <Reasoning
  // duration>, which otherwise measures from its OWN mount and so measured
  // nothing: the disclosure is rendered per-message, and a message does not
  // exist until it has text, by which point the thinking is over. That is how
  // an 11-step, 1m28s turn came to be labelled "Thought for a few seconds".
  //
  // Reasoning time, not turn time — the footer already reports the turn. Time
  // spent waiting on stride_query is not time spent thinking, so the clock
  // only runs between a reasoning part's 'streaming' and 'done' states.
  const [messageThinkingMap, setMessageThinkingMap] = useState<Record<string, number>>({});
  const thinkingMsRef = useRef(0);
  const thinkingSinceRef = useRef<number | null>(null);
  // Placeholder for the chat-triage classifier (frozen/unresponsive/degraded
  // per feedback_crucible_chat_bug_triage memory). The map is consumed by
  // chat-panel and forwarded to ChatMessage; populator will be added when the
  // classifier is wired. Empty record is stable across renders.
  const messageTriageMap = useMemo<Record<string, string>>(() => ({}), []);
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const prevSessionRef = useRef<string | null>(null);
  // Bump to force the history-load effect to re-run for the same
  // sessionId. Used by the resume-stream handler to re-hydrate from DB
  // when the live SSE was killed mid-stream (browser backgrounded the
  // tab → SSE dropped → server-side stream finished + saved → on
  // return, useChat stuck on partial state).
  const [postResumeTick, setPostResumeTick] = useState(0);

  // Create tool handler that routes to content tabs
  const toolHandler = useMemo<ToolHandler>(() => ({
    update_odm_xml: ({
      content,
      isFragment,
      title
    }) => {
      console.log('[Tools] update_odm_xml:', {
        isFragment,
        title,
        contentLength: content.length
      });
      addOrUpdateContentTab('xml', content, title || 'ODM XML', isFragment);
      queueMicrotask(() => {
        window.dispatchEvent(new CustomEvent('crucible:show-xml-tab'));
      });
    },
    show_form_preview: ({
      formXml,
      formName,
      formOid
    }) => {
      console.log('[Tools] show_form_preview:', {
        formName,
        formOid,
        hasXml: !!formXml
      });
      // If the AI sent formXml (new/updated form), add it to the XML tab as a fragment
      // so the form preview can find it. Then switch to the form preview tab.
      if (formXml) {
        addOrUpdateContentTab('xml', formXml, formName || 'Form Preview', true);
      }
      window.dispatchEvent(new CustomEvent('crucible:show-form', {
        detail: {
          formOid,
          formName
        }
      }));
    },
    update_study_config: ({
      config,
      title
    }) => {
      console.log('[Tools] update_study_config:', {
        title
      });
      const jsonContent = typeof config === 'string' ? config : JSON.stringify(config, null, 2);
      addOrUpdateContentTab('json', jsonContent, title || 'Study Configuration');
    },
    add_to_canvas: ({
      content,
      title,
      sectionId
    }) => {
      console.log('[Tools] add_to_canvas:', {
        title,
        sectionId
      });
      addOrUpdateContentTab('markdown', content, title || 'Study Design Canvas', false, sectionId,
      // Tag with the tool that "owns" this canvas write so each tool's
      // ToolCanvas mount filters its own content. Today every mode maps
      // to 'study-design'; per-tool modes (Phase 4) will fan out.
      targetToolForMode(modeRef.current ?? 'concierge'));
    },
    present_build_plan: ({
      structureSummary,
      forms,
      openQuestions,
      title
    }) => {
      console.log('[Tools] present_build_plan:', {
        title,
        formCount: forms?.length ?? 0
      });
      // present_build_plan's backend tool schema no longer asks the model
      // for workUnits/resourceOwnership (see crucible_tools.py) -- the real
      // values always come from POST /api/parallel-build/plan (the same
      // deterministic compute_plan() the build itself uses), fetched here
      // so the ratification screen can never show a plan that diverges
      // from what actually builds. Render immediately with planLoading so
      // the structure summary + open questions aren't blocked on a network
      // round trip; the unit-card grid shows a loading state until the
      // second call below lands.
      const basePlan = {
        structureSummary,
        forms,
        openQuestions,
        chatSessionId: sessionId
      };
      addOrUpdateContentTab('parallel-build-plan',
      // chatSessionId is the REAL chat session id (this hook's sessionId
      // prop) — distinct from the synthetic canvas-tab id ContentTab
      // assigns. PlanRatificationView needs the real id so its
      // SSE /stream calls key into the same backend status registry the
      // get_active_process_status chat tool reads from
      // (context["session_id"]). See tool-canvas.tsx's
      // 'parallel-build-plan' render branch, which parses this back out.
      JSON.stringify({
        ...basePlan,
        workUnits: [],
        resourceOwnership: [],
        planLoading: true
      }), title || 'Build Plan');
      window.dispatchEvent(new CustomEvent('crucible:focus-canvas-tab', {
        detail: {
          tabId: '__build__'
        }
      }));
      void (async () => {
        try {
          const response = await fetch('/api/parallel-build/plan', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            // Envelope, not raw JSON -- see lib/payload-encode.ts. This is
            // the exact call that 403'd on Shay's QuickWave build.
            body: JSON.stringify(wrapPayload({
              forms: forms ?? []
            }))
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const {
            work_units,
            resource_ownership
          } = await response.json();
          addOrUpdateContentTab('parallel-build-plan', JSON.stringify({
            ...basePlan,
            workUnits: work_units,
            resourceOwnership: resource_ownership,
            planLoading: false
          }), title || 'Build Plan');
        } catch (err) {
          // Do NOT fall back to any model-provided guess here -- there
          // isn't one anymore, and silently rendering an empty/wrong plan
          // as if it were real would reintroduce the exact bug this fix
          // exists to eliminate. Surface the error; PlanRatificationView
          // shows a Retry action.
          console.error('[Tools] present_build_plan: /plan fetch failed:', err);
          addOrUpdateContentTab('parallel-build-plan', JSON.stringify({
            ...basePlan,
            workUnits: [],
            resourceOwnership: [],
            planLoading: false,
            planError: err instanceof Error ? err.message : 'Failed to compute build plan'
          }), title || 'Build Plan');
        }
      })();
    },
    generate_study_shell: ({
      protocol_text,
      forms
    }) => {
      console.log('[Tools] generate_study_shell:', {
        formCount: forms?.length ?? 0
      });

      // Task 3.13/3.15. The XML is NOT authored by the model — this calls
      // POST /api/parallel-build/shell, where render_study_shell builds the
      // document in pure Python. Measured on the rig 2026-09-02: ten forms,
      // 11s, validator-clean. The same shell asked for as a chat turn spent
      // 2m33s reasoning about codelist sharing and produced nothing.
      //
      // The call lives here rather than in the tool's server-side execute()
      // because the code that knows whether it failed should be the code
      // that can show the failure. The model's ack is deliberately
      // non-committal for the same reason (see lib/ai/tools.ts) — it never
      // claims a shell exists.
      void (async () => {
        try {
          const result = await generateStudyShell({
            sessionId,
            protocolText: protocol_text,
            forms: (forms ?? []).map(f => ({
              form_oid: f.form_oid,
              form_name: f.form_name,
              crf_text: f.crf_text ?? ''
            }))
          });
          addOrUpdateContentTab('xml', result.shell_xml, 'ODM XML', false);
          window.dispatchEvent(new CustomEvent('crucible:show-xml-tab'));
          console.log('[Tools] generate_study_shell: shell written', {
            bytes: result.shell_xml.length,
            events: result.events?.length ?? 0,
            studyName: result.study_name
          });
        } catch (err) {
          // Never swallow. A 200-with-no-shell already throws
          // `shell_missing` inside generateStudyShell precisely so this
          // cannot surface minutes later as "XML content is empty" from
          // the merge (task 3.6). The typed code is the useful part.
          const code = err instanceof ShellGenerationError ? err.code : 'unknown';
          console.error('[Tools] generate_study_shell failed:', code, err);
          toast.error(code === 'shell_missing' ? 'The shell renderer returned no XML. Nothing was written to the XML tab.' : `Study shell generation failed (${code}).`);
        }
      })();
    },
    display_study: ({
      fileId,
      message
    }) => {
      console.log('[Tools] display_study:', {
        fileId,
        message
      });
      // First check if file is in our uploadedFiles list (by file_id or uploadId)
      const localFile = uploadedFiles.find(f => f.file_id === fileId || f.uploadId === fileId || f.name?.includes(fileId));
      if (localFile?.extracted_content) {
        console.log('[Tools] display_study: Found file locally:', localFile.name);
        addOrUpdateContentTab('xml', localFile.extracted_content, localFile.name || 'Study XML');
        return;
      }
      // Fallback: try to fetch from backend API
      apiClient.getFile(fileId).then(file => {
        // `apiClient.getFile` resolves to `unknown` — its `request<T>` call
        // site names no T — so read the two fields defensively rather than
        // asserting a shape the client never promised.
        const extractedContent = stringField(file, 'extracted_content');
        if (extractedContent) {
          addOrUpdateContentTab('xml', extractedContent, stringField(file, 'filename') || 'Study XML');
        }
      }).catch(err => console.error('[Tools] Failed to fetch file:', err));
    },
    compare_odm: ({
      leftXml,
      rightXml,
      leftLabel,
      rightLabel,
      title
    }) => {
      console.log('[Tools] compare_odm:', {
        leftLabel,
        rightLabel,
        title
      });
      addComparatorTab(leftXml, rightXml, leftLabel, rightLabel, title);
    }
  }), [addOrUpdateContentTab, addComparatorTab, uploadedFiles, sessionId]);

  // Process content: extract tools, execute them, return cleaned display content
  const processContent = useCallback((content: string, messageId: string): string => {
    if (!content) return content;

    // Extract tool invocations and get cleaned content
    const {
      tools,
      cleanedContent
    } = extractToolResults(content);

    // Execute each tool
    for (const tool of tools) {
      try {
        // Normalize and inject sectionId for canvas tools
        if (tool.tool === 'add_to_canvas') {
          if (tool.sectionId?.startsWith('section-')) {
            tool.sectionId = tool.sectionId.replace(/^section-/, '');
          }
          if (!tool.sectionId) {
            tool.sectionId = messageId;
          }
        }
        executeTool(tool, toolHandler);
      } catch (err) {
        console.error('[Tools] Error executing tool:', tool.tool, err);
      }
    }

    // If we routed content to canvas, return a summary link (study-design only)
    const canvasTool = tools.find(t => t.tool === 'add_to_canvas');
    if (canvasTool && modeRef.current !== 'concierge') {
      return `**${canvasTool.title}**\n\n${canvasReferenceMarkdown(modeRef.current ?? 'concierge', messageId)}`;
    }

    // Return cleaned content (or fallback if empty)
    return cleanedContent || 'Content generated. Check the tabs for details.';
  }, [toolHandler]);

  // Memoize files data for transport — include extracted_content when available
  // so the BFF doesn't need to re-fetch from the backend (which may not have
  // the file in memory if it was loaded from versions-index/blob).
  const filesData = useMemo(() => uploadedFiles.filter(f => f.status === 'ready' || f.status === 'stored').map(f => ({
    file_id: f.file_id || f.uploadId,
    name: f.name,
    type: f.type,
    version: f.version,
    ...(f.extracted_content ? {
      extracted_content: f.extracted_content
    } : {})
  })), [uploadedFiles]);

  // Submit-time turn binding — the single source of truth for turn
  // attribution ({sessionId, chatAttemptId, assistantMessageId,
  // userMessageId, startedAt}). Replaces the pendingAssistantId /
  // pendingUserMessage / responseStartTime ref cluster. A resumed or
  // replayed stream has NO active turn, so turn-scoped work (telemetry)
  // is skipped instead of emitting zeroed phantom events.
  //
  // Held through useState's lazy initialiser rather than a ref. The tracker is
  // one stable instance for the life of the mount, and useState's initialiser
  // runs exactly once to produce it; a ref forces a choice between allocating
  // a throwaway tracker on every render and declaring the ref nullable, and
  // the previous code took neither — it declared `useRef<TurnTracker>` and
  // seeded it with a lie. The setter is intentionally unused: this is an
  // identity, not state that changes.
  const [turnTracker] = useState(() => new TurnTracker());

  // Maps AI SDK message IDs → blob storage IDs (set in onFinish)
  const messageIdMapRef = useRef<Record<string, string>>({});

  // Maps blob storage IDs → feedback values (populated from history on load)
  const messageFeedbackMapRef = useRef<Record<string, 'thumbs_up' | 'thumbs_down'>>({});

  // Store filesData in a ref to avoid stale closure issues in transport callback
  const filesDataRef = useRef(filesData);
  filesDataRef.current = filesData;

  // Store sessionId and canvasContent in refs for async callbacks
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  // Mirrored because `aiMessages` is stale inside onFinish — see the auto-title
  // block below, which reads the turn object for the same reason.
  const messageCountRef = useRef(0);
  // Per-session auto-title state, keyed by session id so switching
  // conversations cannot leak one session's count into another — which also
  // means it needs no reset in the session-change effect.
  //
  // This was a single "done" marker, so a conversation was titled once and never
  // again: a session that drifted kept a label describing only its opening
  // exchange. shouldAutoTitle now permits a few retitles after real drift, and
  // none once the user has renamed it by hand.
  const autoTitleStateRef = useRef<Record<string, {
    titleCount: number;
    titledAtMessageCount: number;
    titleLocked: boolean;
  }>>({});
  // canvasContent prop kept for backwards-compatibility on the hook
  // signature, but no longer included in the chat-v3 POST — the BFF
  // resolves canvas content server-side from the session blob. See the
  // body construction below for the incident reference.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Read assessmentContext fresh at send time — it changes as the user runs
  // Compli assessments, and the transport callback is memoized.
  const assessmentContextRef = useRef(assessmentContext);
  assessmentContextRef.current = assessmentContext;
  const activePageRef = useRef(activePage);
  activePageRef.current = activePage;

  // Track tool results received via SSE events (new AI SDK native tool approach)
  const pendingToolResultsRef = useRef<ToolResult[]>([]);

  // Track executed tools for the current message (for display in chat)
  const executedToolsRef = useRef<ExecutedTool[]>([]);
  // The error that ended the current turn, if any. onError fires before
  // onFinish({ isError: true }); the no-text branch reads it to explain the
  // failure instead of blaming the turn (see @/lib/ai/api-error-notice).
  // Turn-scoped: onFinish takes and clears it on every turn (not only the
  // no-text one), and each send resets it, so an error from one turn can
  // never be quoted in a later turn's notice.
  const lastStreamErrorRef = useRef<unknown>(null);

  // Per-toolCallId dedup set for the data-crucible-tool-status consumer.
  // Cleared at the start of each chat send so a new turn starts fresh.
  // Without this, the 11th stride_query of a single turn would be deduped
  // by name and lose all visibility (Bug E).
  const processedToolCallIdsRef = useRef<Set<string>>(new Set());

  // Finalizing state — toggled true when backend emits
  // `data-crucible-text-done` (LLM finished its final answer, but smoothStream
  // is still flushing trailing tokens). Lets the UI show "Finalizing…"
  // distinct from "Generating" so users don't reach for Stop thinking the
  // stream has hung at end-of-text. Reset to false on each new send + on
  // onFinish.
  const [isFinalizing, setIsFinalizing] = useState(false);

  // Store toolHandler in ref for use in fetch wrapper
  const toolHandlerRef = useRef(toolHandler);
  toolHandlerRef.current = toolHandler;

  // Map from message-content prefix → injection context metadata.
  // Populated by injectContextToChat (submit mode) before the send so the
  // aiMessages→messages transform can attach the context pill to the matching
  // user bubble. Silent pin mode uses pinnedChatContextRef instead.
  const injectionByPrefixRef = useRef<Map<string, NonNullable<Message['injectionContext']>>>(new Map());

  // Silently pinned workspace context (Norma codebook viewer). Sent with each
  // turn as pinned_chat_context; never appears as a visible user message.
  const [pinnedChatContext, setPinnedChatContext] = useState<{
    text: string;
    type: NonNullable<Message['injectionContext']>['type'];
    label: string;
  } | null>(null);
  const pinnedChatContextRef = useRef(pinnedChatContext);
  pinnedChatContextRef.current = pinnedChatContext;

  // AI SDK native transport — DefaultChatTransport handles UIMessageStream
  // protocol from toUIMessageStreamResponse(). Tool calls arrive via
  // onToolCall callback on useChat.
  const transport = useMemo(() => new DefaultChatTransport({
    api: '/api/chat-v3',
    prepareSendMessagesRequest({
      messages: msgs
    }) {
      // chat_attempt_id is the diagnostic correlation handle for the
      // "Mode A" failure pattern — frontend saves the user message but
      // the chat-v3 fetch never reaches the server. We persist this id
      // in the user message's message_metadata (visible in chat_messages
      // SQL) AND pass it in the chat-v3 body. Server logs it on receipt.
      // Then a post-hoc query finds user msgs whose attempt_id never
      // appeared in any "Chat request received" log line within ~10s
      // of the user message's created_at — those are confirmed Mode A.
      const chatAttemptId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `attempt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      console.info('[Chat] prepareSendMessagesRequest: enter', {
        chatAttemptId,
        sessionId: sessionIdRef.current,
        messageCount: msgs.length
      });
      const latestMsg = msgs[msgs.length - 1];
      const messageText = getTextFromParts(latestMsg?.parts);
      const assistantMessageId = `assistant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const userMessageId = latestMsg?.id || `user_${Date.now()}`;
      // Bind the turn's identity NOW — completion handlers read this
      // object, never ambient state at completion time.
      turnTracker.begin({
        sessionId: sessionIdRef.current,
        chatAttemptId,
        assistantMessageId,
        userMessageId,
        userText: messageText,
        mode: modeRef.current
      });

      // Save user message to blob (fire and forget). Pass the file_ids
      // for files referenced in this turn so the backend creates
      // file_messages rows in the same transaction as the chat_messages
      // mirror — closes the historical "uploaded but never linked to
      // a message" gap.
      const turnFileIds = (filesDataRef.current ?? []).map((f: {
        file_id?: string;
      }) => f.file_id).filter((id): id is string => typeof id === 'string' && id.length > 0);
      // message_metadata now carries chat_attempt_id + assistant_message_id
      // so we can correlate this user message back to the chat-v3 attempt
      // (or lack thereof) from a pure SQL query.
      apiClient.saveChatMessage(sessionIdRef.current, 'user', messageText, withTurnMode({
        chat_attempt_id: chatAttemptId,
        assistant_message_id: assistantMessageId
      }, modeRef.current), userMessageId, turnFileIds.length > 0 ? turnFileIds : undefined).then(() => {
        console.info('[Chat] user message saved', {
          chatAttemptId,
          userMessageId
        });
      }).catch(err => {
        console.warn('[Chat] Failed to save user message:', {
          chatAttemptId,
          err
        });
      });
      const body = {
        payload_b64: encodePayloadBase64({
          session_id: sessionIdRef.current,
          message: messageText,
          // Strip extracted_content from the payload — it can be 500K chars and
          // causes the WAF bypass base64 payload to exceed WAF size limits.
          // The BFF fetches content server-side via /api/files/{id}/raw.
          uploaded_files: (filesDataRef.current ?? []).map(({
            extracted_content: _omit,
            ...meta
          }: Record<string, unknown>) => meta),
          // canvas_context is now resolved server-side from the session
          // blob (chat-v3 BFF calls /api/sessions/canvas-context/{id}).
          // Sending it inline used to push the POST body past AFD's
          // ~128KB WAF body-inspection cap for sessions with rich
          // canvases — every chat attempt got blocked with an HTML
          // error page (incident 2026-06-05 07:26Z, session_ff29ffa9
          // had a 122KB markdown tab). The blob is updated on every
          // canvas edit, so the server reads a fresh copy.
          user_message_id: userMessageId,
          assistant_message_id: assistantMessageId,
          chat_attempt_id: chatAttemptId,
          mode: modeRef.current,
          active_page: activePageRef.current,
          assessment_context: assessmentContextRef.current,
          pinned_chat_context: pinnedChatContextRef.current?.text,
          ...(modeRef.current === 'study-design' && getActiveStudyContextForRegistration() ? {
            study_context: getActiveStudyContextForRegistration()
          } : {})
        })
      };
      console.info('[Chat] prepareSendMessagesRequest: returning body', {
        chatAttemptId,
        payloadB64Len: body.payload_b64.length
      });
      return {
        body
      };
    }
  }),
  // sessionId accessed via ref. turnTracker is a stable per-mount instance,
  // so listing it satisfies the dependency rule without ever invalidating
  // this memo — it was always a dependency, just hidden behind a ref.
  [encodePayloadBase64, turnTracker]);

  // Fetch model info on mount.
  //
  // Relative URL, not `${apiClient.baseURL}` — this now traverses the BFF
  // (src/app/api/model/info/route.ts), which supplies identity from the
  // server-side session. The backend endpoint is authenticated as of
  // AB#23780, and a direct browser call carried no credential at all.
  useEffect(() => {
    fetch('/api/model/info').then(r => r.ok ? r.json() : null).then(setModelInfo).catch(() => {});
  }, []);

  // AI SDK v6 useChat hook
  const {
    messages: aiMessages,
    setMessages: setAiMessages,
    sendMessage,
    status,
    stop,
    regenerate,
    resumeStream,
    error,
    // The only way out of the SDK's terminal `error` status. Without it a
    // failed stream wedges Send forever — see handleSubmit below.
    clearError
  } = useAIChat({
    // Keying the chat by sessionId lets the SDK build the reconnect URL as
    // `/api/chat-v3/{sessionId}/stream` (the GET reattach handler). Stable per
    // conversation; changes on conversation switch.
    id: sessionId,
    // NOTE: we deliberately do NOT pass `resume: true` here. That flag fires
    // resumeStream() on every mount/re-render with no concurrency guard; our
    // workspace re-mounts the chat several times (StrictMode + navigate-back),
    // so multiple resume requests raced on the SDK's single `activeResponse`
    // slot and threw "Cannot read properties of undefined (reading 'state')".
    // Instead we call resumeStream() exactly once per session in a ref-guarded
    // effect below — single fire, no race.
    transport,
    experimental_throttle: 50,
    onToolCall: ({
      toolCall
    }) => {
      console.log('[AI SDK] onToolCall:', toolCall.toolName, toolCall.input);

      // A call id is claimed only where this handler actually RECORDS an entry
      // — see claimToolCall() at the two push sites below.
      //
      // Claiming here, up front and unconditionally, starved the pills
      // completely. The backend emits a status part for every tool call and
      // that handler is the ONLY place ordinary tools (cdash_domain,
      // maude_search, ctgov_search…) get recorded; it skips anything already
      // claimed. So a blanket claim meant onToolCall consumed the id without
      // recording anything and the status handler then declined to. Live turns
      // ended with executedToolsRef empty, no `Stored N executed tools` log,
      // and no pills — while a reload looked fine, because that path rebuilds
      // from the server's own tally. Confirmed in the rig 2026-08-20 with three
      // onToolCall lines and no corresponding store.
      //
      // The double-count this replaced was real (a single add_to_canvas showed
      // "Canvas ×2" live, "1 tool call" after reload), so the claim has to
      // stay — just scoped to the branches that push.
      //
      // claimForRecording makes the claim and the record one decision, so the
      // two failure modes this code has had — claiming without recording, and
      // recording without claiming — are no longer expressible. See
      // tool-call-ledger.ts.
      const claimToolCall = () => claimForRecording(processedToolCallIdsRef.current, toolCall.toolCallId, toolCall.toolName, executedToolsRef.current.map(t => t.name));

      // route_handoff is a pure-signal UI tool — no side effect, just declares
      // that the work should continue with a different persona. The
      // RouteHandoffCard renders from the executedTools entry below.
      if (toolCall.toolName === 'route_handoff') {
        const input = toolCall.input as {
          targetTool?: string;
          reason?: string;
        };
        if (isHandoffTarget(input?.targetTool)) {
          if (claimToolCall()) executedToolsRef.current.push({
            name: 'route_handoff',
            status: 'success',
            timestamp: Date.now(),
            input: toolCall.input as Record<string, unknown>
          });
        } else {
          console.warn('[AI SDK] Ignoring route_handoff to non-ready target:', input?.targetTool);
        }
        return;
      }
      const toolResult = parseToolResult({
        tool: toolCall.toolName,
        ...(toolCall.input as Record<string, unknown>)
      });
      if (toolResult) {
        try {
          const messageId = turnTracker.current?.assistantMessageId || 'unknown';
          if (toolResult.tool === 'add_to_canvas') {
            if (toolResult.sectionId?.startsWith('section-')) {
              toolResult.sectionId = toolResult.sectionId.replace(/^section-/, '');
            }
            if (!toolResult.sectionId) {
              toolResult.sectionId = messageId;
            }
          }
          const toolStart = Date.now();
          executeTool(toolResult, toolHandlerRef.current);
          console.log('[AI SDK] Tool executed:', toolResult.tool);
          // Capture the full ToolResult (content/title/sectionId for
          // add_to_canvas; formXml/formName for show_form_preview) so
          // onFinish can persist it in message_metadata.tool_results.
          // Without this push, the ref stays empty for the lifetime of
          // the message, and the canvas-reconstruction path on session
          // reload (use-chat.ts:996+) finds nothing to restore. This
          // exact gap was the silent cause of empty canvases despite
          // the chat showing "Show me →" cards.
          pendingToolResultsRef.current.push(toolResult);
          if (claimToolCall()) executedToolsRef.current.push({
            name: toolResult.tool,
            status: 'success',
            timestamp: Date.now(),
            // UI tools run here on the client, so this is their equivalent of
            // the BFF's tool-status event — the only place their arguments are
            // seen. buildToolsUsedEntry caps the values before persisting, and
            // add_to_canvas content is already stored whole by
            // buildPersistableTools, so the cap costs nothing.
            input: toolCall.input as Record<string, unknown>
          });
          trackToolExecution(toolResult.tool, {
            sessionId,
            success: true,
            durationMs: Date.now() - toolStart
          });
        } catch (err) {
          console.error('[AI SDK] Tool execution error:', err);
          if (claimToolCall()) executedToolsRef.current.push({
            name: toolResult.tool,
            status: 'error',
            timestamp: Date.now(),
            // A failed tool is the case an auditor most needs the arguments
            // for, so record them on this branch too.
            input: toolCall.input as Record<string, unknown>
          });
          trackToolExecution(toolResult.tool, {
            sessionId,
            success: false
          });
        }
      }
    },
    // Any data part proves the connection is alive — including transient
    // parts (e.g. the server's data-crucible-keepalive SSE ping). Transient
    // parts deliberately skip message.parts/aiMessages mutation (so they
    // don't pollute chat history), but the stall watchdog below only sees
    // aiMessages changes. Without this, a turn with a long silent
    // "thinking" phase and no earlier tool calls (e.g. digesting several
    // newly-uploaded PDFs before the first token) looks identical to a
    // truly dead connection to the watchdog, which kills it at 120s even
    // though the server's keepalive pings were arriving the whole time.
    onData: () => {
      watchdogRef.current?.progress();
    },
    onFinish: ({
      message,
      isError
    }) => {
      console.log('[AI SDK] onFinish:', message.id, message.role);
      // Take and clear unconditionally: an errored turn that still produced
      // text skips the branch that reads this, and must not leave it behind.
      const streamError = lastStreamErrorRef.current;
      lastStreamErrorRef.current = null;
      // Nothing to clear for the activity label — it is derived from the
      // turn's parts, so it resolves on its own when the turn ends.
      setIsFinalizing(false);

      // Extract custom data parts from the stream (tool stats, usage).
      // streamEnd is LOCAL to this onFinish call — it used to live in a
      // ref, which was needless cross-call state that could leak a late
      // stream-end into the next turn. A local also stays correct for
      // replayed streams (re-saves carry the same stats).
      let streamEnd: {
        usage?: Record<string, number>;
        tool_stats?: {
          count: number;
          tools: string[];
        };
        model?: string;
        provider?: string;
        chat_attempt_id?: string;
      } | null = null;
      if (message.parts) {
        for (const part of message.parts) {
          if ('type' in part && part.type === 'data-crucible-stream-end' && 'data' in part) {
            const data = (part as {
              data?: Record<string, unknown>;
            }).data ?? {};
            console.log('[AI SDK] Stream end data:', data);
            streamEnd = {
              usage: data.usage as Record<string, number> | undefined,
              tool_stats: data.tool_stats as {
                count: number;
                tools: string[];
              } | undefined,
              model: typeof data.model === 'string' ? data.model : undefined,
              provider: typeof data.provider === 'string' ? data.provider : undefined,
              chat_attempt_id: typeof data.chat_attempt_id === 'string' ? data.chat_attempt_id : undefined
            };
          }
          if ('type' in part && part.type === 'data-crucible-tool-status' && 'data' in part) {
            const status = (part as {
              data?: CrucibleToolStatusData;
            }).data;
            // Track completed tool executions for pills + per-tool telemetry.
            // Bug E: backend chat-v3 now emits one part per tool call (data
            // tools included — stride_query, qualio_*, ctgov_*, hubspot_*).
            // Dedup by `toolCallId` so the 11th stride_query of a turn
            // counts as a distinct execution rather than collapsing by name.
            // Fallback to name-dedup for older parts without toolCallId.
            // The `system` sentinel used to be excluded here by name; that check
            // now lives in isUserVisibleTool alongside the control signals, so
            // this surface and the thinking line above agree on one rule.
            if (status && isUserVisibleTool(status.tool)) {
              // Same single decision as onToolCall's push sites — see
              // tool-call-ledger.ts. This is the ONLY place ordinary tools
              // (cdash_domain, maude_search, ctgov_search…) are recorded, so a
              // claim made elsewhere without a matching record silently
              // starves the pills.
              if (claimForRecording(processedToolCallIdsRef.current, status.toolCallId, status.tool, executedToolsRef.current.map(t => t.name))) {
                // See toExecutedToolStatus for why status.status, not a
                // hardcoded 'success', decides this.
                const mappedStatus = toExecutedToolStatus(status.status);
                executedToolsRef.current.push({
                  name: status.tool,
                  status: mappedStatus,
                  timestamp: Date.now(),
                  // Carries through to tool_calls.args via buildToolsUsedEntry.
                  // Data tools run server-side in the BFF, so this event is the
                  // only place the client ever sees their arguments.
                  ...(status.input && typeof status.input === 'object' ? {
                    input: status.input as Record<string, unknown>
                  } : {}),
                  ...(status.details ? {
                    details: status.details
                  } : {})
                });
                // Fire AppInsights per-execution for the data tools we never
                // saw before today. UI tools already fire trackToolExecution
                // from onToolCall — guard against double-counting by tagging
                // these with action='backend' so KQL can distinguish source.
                trackToolExecution(status.tool, {
                  sessionId,
                  success: mappedStatus !== 'error',
                  durationMs: 0
                });
              }
            }
          }
          if ('type' in part && part.type === 'data-crucible-text-done' && 'data' in part) {
            // E1 — backend signaled that the LLM's final answer has been
            // emitted (finishReason='stop'). We flip isFinalizing=true so
            // ChatPanel can show a "Finalizing…" indicator distinct from
            // "Generating" — covers the gap where the user sees text-done
            // and the UI still showing "thinking" because smoothStream is
            // still piping trailing tokens word-by-word.
            // (Live updates also fire from the message-parts watcher
            // below; this handler is the post-stream safety net.)
            setIsFinalizing(true);
          }
        }
      }
      if (message.role === 'assistant' && !processedMessagesRef.current.has(message.id)) {
        processedMessagesRef.current.add(message.id);
        const textContent = getTextFromParts(message.parts);

        // Tools are now processed via SSE stream interception in the custom fetch wrapper.
        // This onFinish handler only needs to:
        // 1. Strip any remaining internal blocks (safety net)
        // 2. Save the message to blob storage
        // 3. Update display if content was routed to canvas

        if (textContent) {
          // Close the turn bound at submit. null = resumed/replayed stream
          // (no submit happened in this mount) — identity falls back to the
          // SDK message id and turn-scoped telemetry is skipped below.
          const turn = turnTracker.complete();
          const messageIdForCanvas = turn?.assistantMessageId || message.id;
          // Record AI SDK ID → blob ID so feedback API calls use the correct ID
          messageIdMapRef.current[message.id] = messageIdForCanvas;

          // Check if any tools executed during this message (tracked via ref)
          const executedTools = pendingToolResultsRef.current;
          const hadCanvasTool = executedTools.some(t => t.tool === 'add_to_canvas');
          pendingToolResultsRef.current = []; // Clear for next message

          // Strip any remaining internal blocks (legacy fallback)
          let displayContent = stripInternalBlocks(textContent);
          // Whether displayContent still IS the turn's text, or has been
          // replaced by something standing in for it. Only the latter may
          // collapse the per-step text parts — see finalizeTurnParts.
          let bodyRewritten = false;

          // The turn narrated a plan and never wrote the answer. The server
          // appended a notice to its own save; this save runs afterwards and
          // overwrites it, so without the same check here the notice is erased
          // and the user is handed the bare preamble again. See
          // @/lib/ai/assistant-text.
          if (shouldShowNoticeFromParts(message.parts)) {
            displayContent = `${displayContent}\n\n${UNFINISHED_TURN_NOTICE}`;
            bodyRewritten = true;
          }

          // ── Content routing: study-design only ──────────────────
          // In concierge mode, the AI's text is the final output — no canvas
          // routing, no link replacement, no table detection. Just save as-is.
          if (modeRef.current !== 'concierge') {
            // If content went to canvas, show a link instead
            if (hadCanvasTool) {
              const canvasTool = executedTools.find(t => t.tool === 'add_to_canvas');
              const title = canvasTool?.title || 'Content';
              let sectionId = canvasTool?.sectionId || messageIdForCanvas;
              if (sectionId.startsWith('section-')) {
                sectionId = sectionId.replace(/^section-/, '');
              }
              displayContent = `**${title}**\n\n${canvasReferenceMarkdown(modeRef.current ?? 'concierge', sectionId)}`;
              bodyRewritten = true;
            }

            // Fallback: also run legacy extraction for any tools not caught via SSE
            if (!executedTools.length && textContent.includes('CRUCIBLE_TOOL_RESULT')) {
              console.log('[AI SDK] Fallback: extracting tools from text content');
              displayContent = processContent(textContent, messageIdForCanvas);
              bodyRewritten = true;
            }

            // Detect tables/headers and auto-route to canvas
            const hasMarkdownTable = displayContent.includes('|') && displayContent.includes('---');
            const hasHtmlTable = /<table[\s>]/i.test(displayContent);
            const hasTable = hasMarkdownTable || hasHtmlTable;
            const hasHeaders = (displayContent.match(/^#+\s/gm) || []).length >= 3;
            if (!hadCanvasTool && (hasTable || hasHeaders)) {
              console.log('[AI SDK] Detected table/structured content not routed to canvas, routing now');
              const canvasContent = displayContent.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))).replace(/["']\s*\}\s*\}\s*$/, '').trim();
              const headerMatch = canvasContent.match(/^#+\s+(.+?)$/m);
              const title = headerMatch?.[1]?.trim() || (hasTable ? 'Query Results' : 'Generated Content');
              addOrUpdateContentTab('markdown', canvasContent, title, false, messageIdForCanvas, targetToolForMode(modeRef.current ?? 'concierge'));
              displayContent = `**${title}**\n\n${canvasReferenceMarkdown(modeRef.current ?? 'concierge', messageIdForCanvas)}`;
              bodyRewritten = true;
            }
          }

          // Save assistant message to blob storage (fire and forget).
          // Persisted metadata reconstructs:
          //   - canvas/form tool payloads on session reload (tool_results)
          //   - assistant message stats display: tokens, elapsed, tool count
          //   - which tools were invoked during this turn
          // Record which canvas received the content. Without it, later reads
          // infer ownership from whichever workspace is open — and route_handoff
          // guarantees that is a different one. See @/lib/ai/canvas-ownership.
          const persistableTools = buildPersistableTools(executedTools, targetToolForMode(modeRef.current ?? 'concierge'));
          const streamData = streamEnd;
          const elapsedMs = turn?.elapsedMs;
          const toolsUsed = executedToolsRef.current.map(buildToolsUsedEntry);
          const metadata: Record<string, unknown> = {};
          if (persistableTools.length > 0) metadata.tool_results = persistableTools;
          if (streamData?.usage) metadata.usage = streamData.usage;
          if (streamData?.tool_stats) metadata.tool_stats = streamData.tool_stats;
          if (elapsedMs) metadata.elapsed_ms = elapsedMs;
          if (toolsUsed.length > 0) metadata.tools_used = toolsUsed;
          // Forward model / attempt id from stream-end so the llm_invocations
          // ledger write (sessions PG mirror) gets a non-null model even when
          // the client save wins the dual-write race over the server save.
          if (streamData?.model) metadata.model = streamData.model;
          if (streamData?.provider) metadata.provider = streamData.provider;
          if (streamData?.chat_attempt_id) metadata.chat_attempt_id = streamData.chat_attempt_id;
          // This save lands AFTER chat-v3's and overwrites the row, so leaving
          // the turn's step structure out here would erase what the server
          // just wrote on every turn that finished in an open tab. See
          // @/lib/ai/turn-reasoning.
          const turnSteps = turnStepsFromParts(message.parts);
          if (turnSteps.length > 0) metadata.turn_steps = turnSteps;
          // Same workspace stamp chat-v3 writes: whichever save wins the
          // race, the reply keeps its mode. The turn's own mode, bound at
          // submit, since the user may have switched workspace since.
          // See @/lib/ai/turn-mode.
          const stamped = withTurnMode(metadata, turn?.mode ?? modeRef.current);
          const toolMetadata = Object.keys(stamped).length > 0 ? stamped : null;
          apiClient.saveChatMessage(turn?.sessionId ?? sessionIdRef.current, 'assistant', textContent, toolMetadata, messageIdForCanvas).catch(err => console.warn('[Chat] Failed to save assistant message:', err));

          // ── Auto-title on first turn ──────────────────────────────
          // The turn object carries the just-sent user text from submit.
          // Reading from it avoids the stale-closure problem with
          // aiMessages inside this callback.
          const sid = turn?.sessionId ?? sessionIdRef.current;
          const firstUserText = turn?.userText?.trim() ?? '';
          const titleState = sid && autoTitleStateRef.current[sid] || {
            titleCount: 0,
            titledAtMessageCount: 0,
            titleLocked: false
          };
          const titleMessageCount = messageCountRef.current;
          if (sid && firstUserText.length >= 2 && textContent.length >= 20 && shouldAutoTitle(titleMessageCount, titleState)) {
            // Optimistic — rolled back below on a retryable failure so a
            // transient skip does not consume one of the few allowed retitles.
            autoTitleStateRef.current[sid] = {
              ...titleState,
              titleCount: titleState.titleCount + 1,
              titledAtMessageCount: titleMessageCount
            };
            fetch(`/api/sessions/${sid}/auto-title`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                user_message: firstUserText,
                assistant_message: textContent
              })
            }).then(async r => {
              // Every outcome is a 200, and several skips carry a `title`
              // field, so the response needs reading rather than a truthiness
              // check on `data.title` — see interpretAutoTitleResponse.
              const outcome = interpretAutoTitleResponse(await r.json().catch(() => null));
              if (outcome.kind === 'titled') {
                console.log('[auto-title] new title:', outcome.title);
                window.dispatchEvent(new CustomEvent('crucible:title-updated', {
                  detail: {
                    sessionId: sid,
                    title: outcome.title
                  }
                }));
              } else if (outcome.kind === 'retry') {
                // Roll the optimistic bump back so the next turn tries again
                // and this attempt does not spend part of the retitle budget.
                console.log('[auto-title] retrying later:', outcome.reason);
                autoTitleStateRef.current[sid] = titleState;
              } else {
                // Locked, or refused on precedence. Asking again would be
                // refused again, so stop for this session — and surface the
                // title that actually stands so the sidebar is not left
                // showing something staler than the index.
                console.log('[auto-title] stopping:', outcome.reason);
                autoTitleStateRef.current[sid] = {
                  ...autoTitleStateRef.current[sid],
                  titleLocked: true
                };
                if (outcome.title) {
                  window.dispatchEvent(new CustomEvent('crucible:title-updated', {
                    detail: {
                      sessionId: sid,
                      title: outcome.title
                    }
                  }));
                }
              }
            }).catch(err => {
              console.warn('[auto-title] failed:', err);
              autoTitleStateRef.current[sid] = titleState; // retry next turn
            });
          }

          // Update the message with cleaned content.
          //
          // Two earlier versions of this both lost something. The first replaced
          // the WHOLE parts array with one text part, discarding every boundary
          // and tool part the stream had built, so the rail appeared while
          // streaming and vanished on completion. The second kept the structural
          // parts but still dropped the per-step text — and the rail labels its
          // nodes from that text, so every label changed the instant a turn
          // settled. finalizeTurnParts keeps both, and collapses only when the
          // body was genuinely replaced rather than cleaned.
          setAiMessages(prev => prev.map(m => m.id === message.id ? {
            ...m,
            parts: finalizeTurnParts(m.parts, displayContent, bodyRewritten)
          } : m));

          // Store executed tools for this message (for display in chat UI)
          // Store under both the AI SDK message ID and our generated ID (for reliable lookup)
          // Filtered at this boundary, not at the five places that push into the
          // ref — see visibleTools.
          const tools = visibleTools<ExecutedTool>(executedToolsRef.current);
          if (tools.length > 0) {
            setMessageToolsMap(prev => ({
              ...prev,
              [String(message.id)]: tools,
              // AI SDK message ID
              [messageIdForCanvas]: tools // Our generated ID
            }));
            console.log(`[AI SDK] Stored ${tools.length} executed tools for message:`, message.id, messageIdForCanvas, tools);
          }
          executedToolsRef.current = []; // Clear for next message

          // Store stream_end stats (usage, tool_stats, elapsed time) for this message.
          // streamData/elapsedMs are already captured above for persistence — reuse them.
          if (streamData || elapsedMs) {
            const stats = {
              ...streamData,
              elapsed_ms: elapsedMs
            };
            setMessageStatsMap(prev => ({
              ...prev,
              [String(message.id)]: stats,
              [messageIdForCanvas]: stats
            }));
          }

          // Close any span still open (a turn that ended mid-reasoning) before
          // reading the accumulator, then hand the total to this message and
          // reset for the next turn. Keyed both ways for the same reason the
          // stats above are: the canvas id and the message id are both looked
          // up by different surfaces.
          //
          // Left ABSENT rather than written as 0 when nothing was measured —
          // a turn with no reasoning, or one restored from history. The
          // trigger renders undefined as "Thought" with no figure, and a
          // fabricated "0 seconds" would be worse than saying nothing.
          if (thinkingSinceRef.current !== null) {
            thinkingMsRef.current += Date.now() - thinkingSinceRef.current;
            thinkingSinceRef.current = null;
          }
          if (thinkingMsRef.current > 0) {
            const seconds = Math.max(1, Math.round(thinkingMsRef.current / 1000));
            setMessageThinkingMap(prev => ({
              ...prev,
              [String(message.id)]: seconds,
              [messageIdForCanvas]: seconds
            }));
          }
          thinkingMsRef.current = 0;

          // AB#19124 slice 3 — `chat_stream_complete` lifecycle event.
          // Closes the inference-from-logs gap: per-message latency,
          // token cost (including Anthropic cache_read), and tool count
          // become queryable customMeasurements in AppInsights without
          // having to dig through AppTraces.
          //
          // Turn-gated: identity comes from the turn bound at submit, so a
          // resumed/replayed stream (turn == null) emits NOTHING instead of
          // a phantom event with zeroed measurements attributed to whatever
          // state was ambient (the 2026-06-11 misattribution bug).
          if (turn) {
            const u = streamData?.usage;
            const ts = streamData?.tool_stats;
            trackEvent('chat_stream_complete', {
              sessionId: turn.sessionId,
              chatAttemptId: turn.chatAttemptId,
              mode: modeRef.current ?? 'concierge'
            }, {
              elapsedMs: turn.elapsedMs,
              inputTokens: u?.input_tokens ?? 0,
              outputTokens: u?.output_tokens ?? 0,
              cacheReadTokens: u?.cache_read_input_tokens ?? 0,
              cacheCreateTokens: u?.cache_creation_input_tokens ?? 0,
              toolCount: ts?.count ?? 0
            });
          }
        } else {
          // The turn produced no text anywhere. Everything above is gated on
          // textContent, so without this branch the browser does nothing at
          // all: no render, no pills, and the refs below stay dirty. The user
          // watches a turn run for minutes and is left with silence.
          //
          // chat-v3's onFinish has already persisted EMPTY_TURN_NOTICE
          // server-side (see @/lib/ai/assistant-text), so this only mirrors
          // that into the live view — deliberately no save of our own, which
          // would overwrite the server's row with weaker metadata.
          // A turn the API rejected also has no text, but it is not an empty
          // turn: showing EMPTY_TURN_NOTICE there blamed "a long investigation"
          // for what was a 400 (2026-10-01: a truncated PDF). chat-v3 saves the
          // same api-error notice server-side, so the reload matches this view.
          const noticeText = isError ? chatApiErrorNotice(streamError) : EMPTY_TURN_NOTICE;
          console.warn(isError ? '[AI SDK] Turn ended in an API error — showing the error notice' : '[AI SDK] Turn produced no assistant text — showing notice');
          setAiMessages(prev => prev.map(m => m.id === message.id ? {
            ...m,
            parts: [{
              type: 'text' as const,
              text: noticeText
            }]
          } : m));
          // Keep the pills: a turn that burned 19 tool calls should still show
          // what it did, and that evidence is the first thing worth reading
          // when this happens.
          const tools = visibleTools<ExecutedTool>(executedToolsRef.current);
          if (tools.length > 0) {
            setMessageToolsMap(prev => ({
              ...prev,
              [String(message.id)]: tools
            }));
          }
          // Both refs are turn-scoped. The textContent branch clears them on
          // its way out; skipping that here would attribute this turn's tools
          // to the next message.
          executedToolsRef.current = [];
          pendingToolResultsRef.current = [];
        }
      }
    },
    onError: err => {
      const msg = err instanceof Error ? err.message : String(err);
      lastStreamErrorRef.current = err;

      // Session-expired short-circuit. When the browser session lapses
      // (IdleGuard 30-min HIPAA timeout, manual sign-out, server-side
      // token-store wipe after a container restart), the resumable-stream
      // poll at /api/chat-v3/{sessionId}/stream returns the specific shape
      // `{"error":"Authentication required"}` every cycle. That's NOT a
      // Mode B transport failure — it's "user is signed out", which the
      // middleware / page-level auth guard already handles by redirecting
      // to /login. Toasting every poll would flood the UI; warn-log once
      // and bail.
      //
      // NARROW MATCH ONLY. An earlier cut also bailed on any message
      // containing "unauthorized" (case-insensitive), which silently
      // swallowed downstream-service 401s — e.g. Vercel AI SDK
      // `AI_APICallError: Unauthorized` when the Azure Foundry gateway
      // briefly 401s the static API key (observed 2026-06-03 07:10:42 +
      // 07:11:10 during a ~30s key-rotation window — chat appeared to
      // hang silently with zero UI feedback). Those need to surface as
      // real errors. The session-expired case has a very specific server
      // payload — match only that.
      const isSessionExpired = msg.includes('"error":"Authentication required"') || msg.includes('Authentication required');
      if (isSessionExpired) {
        console.warn('[AI SDK] session expired — deferring to auth guard');
        return;
      }
      const activeTurn = turnTracker.current;
      console.error('[AI SDK] Error:', msg, err);
      console.error('[AI SDK] Session context:', {
        sessionId,
        chatAttemptId: activeTurn?.chatAttemptId,
        pendingAssistantId: activeTurn?.assistantMessageId,
        messagesCount: aiMessages.length,
        isConnected,
        errorType: err instanceof Error ? err.name : typeof err
      });
      trackChatError(err, {
        sessionId: activeTurn?.sessionId ?? sessionId,
        chatAttemptId: activeTurn?.chatAttemptId,
        messageCount: aiMessages.length,
        action: 'chat_stream'
      });
      // Surface to the user. Before this, transport-layer errors (Anthropic
      // API rejections, AFD/WAF disconnects, 499 client-timeout chains, etc.)
      // only showed up in the console and the user saw a silent void.
      // A 2026-06-02 session was the clearest case: 5 streaming chat-v3
      // calls returned HTTP 499 over 80 seconds with zero UI signal that
      // anything had gone wrong.
      //
      // Toast contract:
      //   - Persistent (Infinity duration) — don't auto-dismiss. The user
      //     needs time to read and act. toast() normalizes this into an
      //     in-chrome Dismiss cancel button (no corner ×).
      //   - closeButton: true — kept as a signal for that dismiss button;
      //     never the floating Sonner × (older 8s + click-to-dismiss made
      //     long descriptions unreadable).
      //   - Classify the failure type from the payload shape and surface
      //     an actionable hint instead of dumping the raw error.
      const looksLikeWafBlock = msg.includes('<!DOCTYPE') || msg.includes('The request is blocked') || msg.includes('Service unavailable');
      const looksLikeTokenExpired = msg.includes('Token has expired') || msg.includes('Bearer token validation failed');
      let title = 'Chat request failed';
      let description: string;
      if (looksLikeWafBlock) {
        title = 'Message blocked by the gateway';
        description = 'Azure’s gateway (AFD/WAF) rejected this request before it reached the server — ' + 'often a transient hiccup. Resend, or open a new conversation. ' + 'If it keeps happening and your message is very long, shorten it or attach the text ' + 'as a file (uploads bypass this limit). The AFD correlation ID is in the browser console.';
      } else if (looksLikeTokenExpired) {
        title = 'Your session token has expired';
        description = 'Reload the page (Cmd-Shift-R) to refresh your sign-in. Then resend.';
      } else {
        // Generic — show the raw error message but keep the full text
        // available (the description field can wrap; no truncation).
        description = msg;
      }
      toast.error(title, {
        description,
        duration: Infinity,
        closeButton: true
      });
    }
  });

  // Single-fire stream resume per session. Replaces useChat's `resume: true`
  // flag (which fires with no concurrency guard and raced on activeResponse).
  // Guarded by a ref so StrictMode double-invokes and re-renders can't fire
  // more than once per sessionId. On mount/conversation-switch we attempt
  // exactly one reattach to an in-flight stream; the GET returns 204 when
  // nothing is live (no-op), or replays the live stream when one exists.
  const resumeAttemptedRef = useRef<string | null>(null);
  const userAbortedRef = useRef<string | null>(null);

  // Keep the mirrored message count current. Assigning during render is not an
  // option here: aiMessages is destructured further down than the ref block, and
  // onFinish reads the ref precisely because the closure over aiMessages is
  // stale by the time it runs.
  useEffect(() => {
    messageCountRef.current = aiMessages.length;
  }, [aiMessages.length]);
  useEffect(() => {
    // isConnected is authReady from the provider — a reattach fired before
    // the auth session exists 401s every time (pre-login noise in the
    // failure monitors). Wait for auth; the guard ref is only set when we
    // actually fire, so the attempt still happens once auth lands.
    if (!sessionId || !isConnected) return;
    if (userAbortedRef.current === sessionId) return;
    if (resumeAttemptedRef.current === sessionId) return;
    resumeAttemptedRef.current = sessionId;
    (async () => {
      // Dead-reattach bound. A reattach to a genuinely live stream replays
      // its buffered prefix immediately (first chunk in <2s), flipping the
      // SDK status submitted → streaming. A reattach to a dead-but-present
      // stream gets a 200 that never sends a byte — the SDK then sits in
      // 'submitted' FOREVER, isGenerating stays true, and the composer is
      // wedged with zero visible network activity (2026-06-11 incident).
      // resumeStream() also never resolves in that case, so the DB
      // re-hydration below never ran either. If no chunk arrives within
      // 15s, abort: stop() settles status to 'ready' and the await resolves.
      const deadReattachTimer = setTimeout(() => {
        if (statusRef.current === 'submitted') {
          console.warn('[Chat] resume reattach produced no data in 15s — aborting dead stream');
          trackEvent('chat_resume_dead_reattach', {
            sessionId
          });
          stop();
        }
      }, 15_000);
      try {
        await resumeStream?.();
      } catch (err) {
        console.warn('[Chat] resumeStream attempt failed (non-fatal):', err);
      } finally {
        clearTimeout(deadReattachTimer);
      }
      // After resume settles, force a re-hydration from DB. Two cases:
      //   - 204 (no live stream to reattach): server-side stream
      //     finished while we were away. DB has the final assistant
      //     message; useChat's aiMessages is stuck on whatever was in
      //     state before the click-away. Without this re-fetch, the UI
      //     never shows the completed response — the bug
      //     reported as "stream did not resume on a conversation I
      //     clicked away from".
      //   - 200 (stream was live): aiMessages just got populated by
      //     the live SSE. DB has the same content (or will, momentarily).
      //     The history-load's wipe-on-empty path is now guarded to
      //     skip wipes on same-session refreshes, so a transient empty
      //     DB read can't clobber the live state.
      // Bumping a state tick re-fires the main history-load effect for
      // the same sessionId, which then dedups + setAiMessages from DB.
      setPostResumeTick(n => n + 1);
    })();
    // `stop` is read by the dead-reattach timer. Declaring it cannot cause a
    // second reattach: resumeAttemptedRef is set before the async work starts
    // and short-circuits every later run for the same sessionId.
  }, [sessionId, resumeStream, isConnected, stop]);

  // Derive isGenerating from status
  const isGenerating = status === 'streaming' || status === 'submitted';

  // Status mirror for async callbacks (the resume dead-reattach timer reads
  // this — a closure over `status` would be stale by the time it fires).
  const statusRef = useRef(status);
  statusRef.current = status;

  // ── Stream watchdog: every non-terminal state is bounded ──────────────
  // Standard: every async operation reaches a surfaced terminal state with
  // a recovery affordance. If the stream produces NOTHING for 2 minutes
  // (dead reattach, silent transport loss, hung backend), stop() it — the
  // SDK's abort path settles status to 'ready', which un-wedges the
  // composer — and tell the user how to recover. 120s comfortably exceeds
  // the longest observed organic inter-chunk gap (long tool rounds emit
  // tool-status parts every ~15-25s; first-token on heavy cache writes
  // stays under ~60s).
  const watchdogRef = useRef<StreamWatchdog | null>(null);
  if (!watchdogRef.current) {
    watchdogRef.current = new StreamWatchdog({
      stallMs: 120_000,
      onStall: () => {
        console.warn('[Chat] stream stalled — no data for 120s; stopping to free the composer');
        trackEvent('chat_stream_stalled', {
          sessionId: sessionIdRef.current,
          status: statusRef.current
        });
        stopRef.current?.();
        toast.error('The response stalled', {
          description: 'No data arrived for 2 minutes, so the stream was stopped. ' + 'Your conversation is intact — resend your last message to retry.',
          duration: Infinity,
          closeButton: true
        });
      }
    });
  }
  const stopRef = useRef(stop);
  stopRef.current = stop;
  useEffect(() => {
    if (isGenerating) {
      watchdogRef.current?.arm();
      return () => watchdogRef.current?.disarm();
    }
  }, [isGenerating]);

  // Surface in-flight generation to workspace (conversation-list indicator).
  useEffect(() => {
    reportStreamingSession?.(sessionId, isGenerating);
  }, [sessionId, isGenerating, reportStreamingSession]);

  // Switching away mid-stream: keep the background row marked until Redis
  // reports the stream finished (polled via /active).
  useEffect(() => {
    return () => {
      const s = statusRef.current;
      if (s === 'streaming' || s === 'submitted') {
        reportStreamingSession?.(sessionId, true);
      }
    };
  }, [sessionId, reportStreamingSession]);

  // Every message mutation while generating (token append, tool-status data
  // part, step boundary) counts as progress.
  useEffect(() => {
    if (isGenerating) watchdogRef.current?.progress();
  }, [aiMessages, isGenerating]);

  // Reset idle-timeout clock while the AI is actively streaming — prevents
  // logout during long responses where the user isn't touching the mouse.
  useEffect(() => {
    if (isGenerating) {
      document.dispatchEvent(new CustomEvent('crucible:activity'));
    }
  }, [isGenerating]);

  // Load chat history from backend on session change
  useEffect(() => {
    console.log('[AI SDK] History effect running:', {
      isConnected,
      sessionId,
      prevSession: prevSessionRef.current
    });
    if (!isConnected || !sessionId) {
      console.log('[AI SDK] Skipping history load - not connected or no sessionId');
      return;
    }
    const isSessionChange = prevSessionRef.current !== null && prevSessionRef.current !== sessionId;
    prevSessionRef.current = sessionId;
    processedMessagesRef.current = new Set();

    // Always clear messages when session changes (prevents cross-session bleed)
    if (isSessionChange) {
      console.log(`[AI SDK] Session changed to ${sessionId}, aborting stream + clearing messages`);
      userAbortedRef.current = null;
      resumeAttemptedRef.current = null;
      stop();
      setAiMessages([]);
      setMessageHistory([]);
      setMessageToolsMap({});
      setMessageStatsMap({});
      setMessageThinkingMap({});
      messageFeedbackMapRef.current = {};
      setPinnedChatContext(null);
    }

    // Capture sessionId for the closure so stale fetches don't overwrite
    const fetchSessionId = sessionId;
    console.log('[AI SDK] Fetching chat messages for session:', sessionId);
    apiClient.getChatMessages(sessionId).then(saved => {
      // Guard: if user switched sessions while this fetch was in flight, discard
      if (prevSessionRef.current !== fetchSessionId) {
        console.log('[AI SDK] Discarding stale fetch for', fetchSessionId);
        return;
      }
      console.log('[AI SDK] getChatMessages response:', saved);
      if (saved?.length) {
        // Deduplicate by message ID (blob storage can have duplicates)
        const seen = new Set<string>();
        saved = saved.filter(msg => {
          if (seen.has(msg.id)) return false;
          seen.add(msg.id);
          return true;
        });

        // Restore feedback state from blob storage so it survives page reload
        messageFeedbackMapRef.current = {};
        saved.forEach(msg => {
          // Narrowed rather than trusted: `feedback` arrives off the wire
          // as a string, and the map it feeds is typed to two values. The
          // `any` this replaces let a third value through into typed state.
          if (msg.role === 'assistant' && (msg.feedback === 'thumbs_up' || msg.feedback === 'thumbs_down')) {
            messageFeedbackMapRef.current[msg.id] = msg.feedback;
          }
        });
        const formattedMessages = saved.map(msg => {
          const id = `db_${msg.id}`;
          let content = msg.content;
          if (msg.role === 'assistant') {
            const metadata = msg.message_metadata;
            const isConcierge = modeRef.current === 'concierge';
            if (isConcierge) {
              // ── Concierge: show content inline, no canvas routing ──
              // If metadata has tool_results with actual content, use that
              // (the saved message text may be a canvas link from before the fix)
              if (metadata?.tool_results) {
                for (const toolResult of metadata.tool_results) {
                  if (toolResult.tool === 'add_to_canvas' && toolResult.content) {
                    content = toolResult.content;
                  }
                }
              }
              // Strip any leftover canvas links from pre-fix saved messages
              // Strip any "[View in X Canvas](...)" link from legacy or
              // current rewrites — Concierge surfaces content inline and
              // doesn't need the canvas reference markdown.
              if (/\[View in [^\]]*Canvas\]/.test(content)) {
                content = content.replace(/\n*\[View in [^\]]*Canvas\]\([^)]*\)/g, '').trim();
              }
            } else {
              // ── Study Design: restore canvas tabs + show links ──
              if (metadata?.tool_results) {
                for (const toolResult of metadata.tool_results) {
                  if (toolResult.tool === 'add_to_canvas' && toolResult.content) {
                    let sectionId = toolResult.sectionId || msg.id;
                    if (sectionId.startsWith('section-')) {
                      sectionId = sectionId.replace(/^section-/, '');
                    }
                    // The owner recorded at write time, not the workspace
                    // being viewed from. Falls back to the viewer only for
                    // blobs written before the field existed.
                    const canvasOwner = canvasOwnerFor(toolResult, targetToolForMode(modeRef.current ?? 'concierge'));
                    addOrUpdateContentTab('markdown', toolResult.content, toolResult.title || 'Canvas', false, sectionId, canvasOwner);
                    content = `**${toolResult.title || 'Content'}**\n\n${canvasLinkForTool(canvasOwner, sectionId)}`;
                  } else if (toolResult.tool === 'show_form_preview') {
                    window.dispatchEvent(new CustomEvent('crucible:show-form', {
                      detail: {
                        formName: toolResult.formName
                      }
                    }));
                  }
                }
              } else {
                content = processContent(content, msg.id);
                const hasTable = content.includes('|') && content.includes('---');
                const hasHeaders = (content.match(/^#+\s/gm) || []).length >= 3;
                if (hasTable || hasHeaders) {
                  const headerMatch = content.match(/^#+\s+(.+?)$/m);
                  const summary = headerMatch?.[1]?.trim() || (hasTable ? 'Table generated' : 'View structured content');
                  content = `**${summary}**\n\n${canvasReferenceMarkdown(modeRef.current ?? 'concierge', msg.id)}`;
                }
              }
            }
          }

          // The turn's structure goes back in FRONT of the text: step
          // boundaries, each step's reasoning, and the tools it called.
          // The text then lands in the final bucket, so groupPartsIntoSteps
          // rebuilds the same rail a settled live turn shows. Without this a
          // reloaded turn is a single text part — no reasoning disclosure,
          // no rail, no per-step badges.
          const restoredParts = msg.role === 'assistant' ? restoredTurnParts(msg.message_metadata) : [];
          return {
            id,
            role: msg.role as 'user' | 'assistant',
            parts: [...restoredParts, {
              type: 'text' as const,
              text: content
            }],
            createdAt: msg.created_at ? new Date(msg.created_at) : undefined
          };
        });
        setAiMessages(formattedMessages);

        // Restore messageStats from saved metadata so historical assistant
        // messages show tokens/elapsed/tool counts after reload (matches
        // what the user saw live when the turn completed).
        // Extracted to @/lib/ai/restore-message-maps so the key shape is
        // testable: these were keyed db_<id> while every surface looks up the
        // stripped id, so pills and the stats footer vanished on reload.
        const {
          tools: restoredTools,
          stats: restoredStats
        } = buildRestoredMaps(saved);
        if (Object.keys(restoredStats).length > 0) {
          setMessageStatsMap(prev => ({
            ...prev,
            ...restoredStats
          }));
        }
        if (Object.keys(restoredTools).length > 0) {
          setMessageToolsMap(prev => ({
            ...prev,
            ...restoredTools
          }));
        }
        console.log(`[AI SDK] Loaded ${formattedMessages.length} messages (${Object.keys(restoredStats).length} with stats)`);
      } else {
        // No saved messages from DB. Only wipe local state on a
        // genuine session change — for a same-session refresh (the
        // post-resume tick path), we may already have live-stream
        // content that hasn't been persisted yet. Wiping on empty
        // there would clobber the live state we're trying to preserve.
        if (isSessionChange) {
          setAiMessages([]);
        }
      }
    }).catch(err => {
      const status = (err as Error & {
        status?: number;
      })?.status;
      if (status === 403) {
        window.dispatchEvent(new CustomEvent('crucible:session-forbidden', {
          detail: {
            sessionId: fetchSessionId
          }
        }));
        return;
      }
      console.error('[AI SDK] Failed to load chat history:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, sessionId, setAiMessages, postResumeTick]);

  // Track user message history for prompt navigation
  useEffect(() => {
    const userMessages = aiMessages.filter(m => m.role === 'user').map(m => getTextFromParts(m.parts)).filter(Boolean);
    setMessageHistory(prev => {
      const newHistory = [...new Set([...prev, ...userMessages])];
      return newHistory.slice(-50);
    });
  }, [aiMessages]);

  // Submit handler. Returns whether the message was ACCEPTED — callers keep
  // the draft in the composer when it wasn't. A silent drop here was the
  // 2026-06-11 "I can't hit send" experience: the guard swallowed the
  // message, the input cleared anyway, and nothing told the user why.
  const handleSubmit = useCallback((message: string): boolean => {
    if (!message.trim()) return false;
    // `error` is not "busy" — it is "the last turn is dead". The SDK parks
    // the chat there after a stream failure and never leaves on its own, so
    // the `status !== 'ready'` guard below used to reject every subsequent
    // send while the toast told the user to "press send again in a moment",
    // which could never work. Measured: 11 of 11 send-blocks in 30 days were
    // status:error, and reload was the only exit. clearError() puts the SDK
    // back to `ready`; sendMessage() then drives makeRequest() as normal
    // (it sets status itself and does not read the pre-call status).
    if (status === 'error') {
      console.warn('[AI SDK] recovering from wedged error state and resending');
      trackEvent('chat_send_error_recovered', {
        sessionId,
        mode: modeRef.current ?? 'concierge'
      });
      clearError();
    } else if (status !== 'ready') {
      console.warn('[AI SDK] send blocked — stream status:', status);
      trackEvent('chat_send_blocked', {
        sessionId,
        status,
        mode: modeRef.current ?? 'concierge'
      });
      toast.info('Still finishing the previous response', {
        description: 'Your message is kept in the box — press send again in a moment.'
      });
      return false;
    }
    console.log('[AI SDK] Sending:', message.substring(0, 50) + '...');
    // Reset per-turn state — last turn's finalizing flag must not bleed
    // into this one's "Generating" indicator, and the toolCallId dedup
    // set starts fresh so each turn's tools count cleanly.
    setIsFinalizing(false);
    processedToolCallIdsRef.current = new Set();
    userAbortedRef.current = null;
    // An error that arrived without a matching onFinish (a resume poll, an
    // aborted turn) must not be read as this turn's failure.
    lastStreamErrorRef.current = null;
    // Belt and braces with the reset in onFinish: an aborted or errored
    // turn never reaches onFinish, and a leftover accumulator would be
    // charged to the NEXT turn's disclosure.
    thinkingMsRef.current = 0;
    thinkingSinceRef.current = null;
    // AB#19124 slice 3 — `chat_send` lifecycle event. The "user just
    // pressed send" anchor for end-to-end chat-turn correlation in
    // AppInsights. Pair this with chat_stream_complete + chat_error
    // to build a per-turn funnel (sends / completes / errors per mode).
    trackEvent('chat_send', {
      sessionId,
      mode: modeRef.current ?? 'concierge'
    }, {
      messageLength: message.length
    });
    sendMessage({
      text: message
    });
    return true;
  }, [status, sendMessage, sessionId, clearError]);

  // Attach workspace context to chat.
  // - pin (default for codebook-viewer): store for silent model injection on
  //   subsequent turns — no visible user bubble, no handleSubmit.
  // - submit (Compli / Marq share buttons): existing visible turn + pill.
  const injectContextToChat = useCallback((text: string, type: NonNullable<Message['injectionContext']>['type'], label: string, options?: {
    mode?: 'submit' | 'pin';
  }) => {
    const mode = resolveContextInjectionMode(type, options?.mode);
    if (mode === 'pin') {
      setPinnedChatContext({
        text,
        type,
        label
      });
      return;
    }
    const prefix = text.slice(0, 80);
    injectionByPrefixRef.current.set(prefix, {
      type,
      label
    });
    if (!handleSubmit(text)) injectionByPrefixRef.current.delete(prefix);
  }, [handleSubmit]);

  // Stop generation — abort client transport, cancel server stream, and clear
  // Redis resumable pointer so GET reattach cannot revive a stopped turn.
  const handleStopGeneration = useCallback(() => {
    const sid = sessionIdRef.current;
    console.log('[AI SDK] Stopping', {
      sessionId: sid
    });
    userAbortedRef.current = sid;
    resumeAttemptedRef.current = sid;
    stop();
    setIsFinalizing(false);
    reportStreamingSession?.(sid, false);
    void fetch(`/api/chat-v3/${sid}/abort`, {
      method: 'POST'
    }).catch(err => {
      console.warn('[AI SDK] abort endpoint failed (non-fatal):', err);
    });
  }, [stop, reportStreamingSession]);

  // Reset chat state
  const resetChat = useCallback(() => {
    console.log('[AI SDK] Resetting');
    stop();
    setAiMessages([]);
    setMessageHistory([]);
    processedMessagesRef.current = new Set();
    prevSessionRef.current = null;
    // Also clear the resume-attempt guard + tick so a fresh resume
    // can fire on the next sessionId.
    resumeAttemptedRef.current = null;
    setPostResumeTick(0);
    setPinnedChatContext(null);
  }, [setAiMessages, stop]);

  // Convert to our Message format, preserving AI SDK parts for streaming state.
  // Normalize IDs to blob storage IDs so feedback API calls work:
  //   - Live messages: map SDK random ID → pre-generated assistant_xxx ID
  //   - Historical messages: strip the db_ prefix added during load
  const messages: Message[] = aiMessages.map(m => {
    const blobId = messageIdMapRef.current[m.id] ?? String(m.id).replace(/^db_/, '');
    const content = getTextFromParts(m.parts);
    const injectionContext = m.role === 'user' ? injectionByPrefixRef.current.get(content.slice(0, 80)) ?? undefined : undefined;
    return {
      id: blobId,
      role: m.role as 'user' | 'assistant',
      content,
      parts: m.parts as MessagePart[],
      // `createdAt` is not an SDK field — the history loader above attaches
      // it to each rebuilt message (`new Date(msg.created_at)`) so a
      // reloaded turn keeps the time it was actually written. A live
      // message has none, and falls back to now.
      timestamp: (m as {
        createdAt?: Date;
      }).createdAt?.toISOString() || new Date().toISOString(),
      feedback: messageFeedbackMapRef.current[blobId] ?? null,
      ...(injectionContext ? {
        injectionContext
      } : {})
    };
  }).filter(m => m.role === 'user' || m.content.trim());

  // AB#19124 slice 3 — `chat_retry_clicked` lifecycle event. Wraps
  // AI SDK's regenerate() so every callsite that consumes `reload`
  // (mainchat retry button, the seven workspace chat-panel onRetry
  // props) attributes through this single instrumented entry point.
  const trackedRegenerate = useCallback(async () => {
    trackEvent('chat_retry_clicked', {
      sessionId,
      mode: modeRef.current ?? 'concierge'
    }, {
      messageCount: aiMessages.length
    });
    return regenerate();
  }, [regenerate, sessionId, aiMessages.length]);

  // What this turn is currently doing. Derived, never stored: `onToolCall`
  // fires only once a tool call is COMPLETE, so a stored label had nothing to
  // overwrite it during a long input stream and outlived its own step. Read off
  // `aiMessages` rather than `messages` because a textless assistant turn is
  // filtered out of the latter — which is exactly when a tool is in flight.
  const turnActivity = useMemo<TurnActivity | null>(() => {
    if (status !== 'streaming' && status !== 'submitted') return null;
    const lastMsg = aiMessages[aiMessages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return {
      kind: 'thinking'
    };
    return deriveTurnActivity(lastMsg.parts);
  }, [aiMessages, status]);

  // When the current turn began, for the elapsed segment of the status line.
  // A timestamp rather than a running count on purpose: a per-second counter
  // here would re-render every consumer of this hook once a second. The status
  // line owns its own tick, so the churn stays inside that one small component.
  const [turnStartedAt, setTurnStartedAt] = useState<number | null>(null);
  useEffect(() => {
    if (!isGenerating) {
      setTurnStartedAt(null);
      return;
    }
    setTurnStartedAt(prev => prev ?? Date.now());
  }, [isGenerating]);

  // Extract live reasoning text from the streaming assistant message.
  // The assistant message is filtered out of `messages` during thinking
  // (no text content yet), but the raw AI SDK message has reasoning parts.
  const streamingReasoning = useMemo(() => {
    if (status !== 'streaming') return null;
    const lastMsg = aiMessages[aiMessages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return null;
    const reasoningParts = (lastMsg.parts || []).filter(isReasoningUIPart).filter(p => p.text);
    if (reasoningParts.length === 0) return null;
    return reasoningParts[reasoningParts.length - 1].text;
  }, [aiMessages, status]);

  // The streaming assistant message's raw parts, for the step rail during the
  // thinking phase. Exists for exactly the reason streamingReasoning does: the
  // `messages` transform below ends in
  //   .filter((m) => m.role === 'user' || m.content.trim())
  // so a textless assistant turn is not in `messages` at all, and anything the
  // transcript renders per-message — the step rail, the reasoning disclosure,
  // its duration — cannot mount until the first text token lands.
  //
  // That was invisible until Sonnet 5. Claude 4.6 wrote a preamble before every
  // tool call, so the message gained text within a second or two. Sonnet 5
  // writes none: measured on the rig 2026-09-11, a 9-step turn reported
  // textChars=0 on steps 1-8 and only produced text on step 9, so the whole
  // tool loop ran with nothing on screen.
  //
  // Returns the parts by reference; MessageStepRail's own groupPartsIntoSteps
  // returns null until a step-start actually arrives, so there is no empty
  // rail flash at turn start.
  const streamingParts = useMemo(() => {
    if (status !== 'streaming') return null;
    const lastMsg = aiMessages[aiMessages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return null;
    return (lastMsg.parts ?? null) as MessagePart[] | null;
  }, [aiMessages, status]);

  // Is a reasoning part mid-flight right now? The AI SDK stamps
  // state:'streaming' on reasoning-start and state:'done' on reasoning-end
  // (ai/dist — see the UI message stream reducer), so this is the provider's
  // own boundary rather than a guess inferred from the text growing.
  const isReasoningActive = useMemo(() => {
    if (status !== 'streaming') return false;
    const lastMsg = aiMessages[aiMessages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return false;
    return (lastMsg.parts || []).some(p => isReasoningUIPart(p) && p.state === 'streaming');
  }, [aiMessages, status]);

  // Accumulate across steps. A turn opens and closes several reasoning parts,
  // with tool calls in the gaps; only the in-flight spans are added, so the
  // total is thinking time and not wall-clock.
  useEffect(() => {
    if (isReasoningActive) {
      if (thinkingSinceRef.current === null) thinkingSinceRef.current = Date.now();
      return;
    }
    if (thinkingSinceRef.current !== null) {
      thinkingMsRef.current += Date.now() - thinkingSinceRef.current;
      thinkingSinceRef.current = null;
    }
  }, [isReasoningActive]);

  // E1 + E3 live finalizing detector. The backend writes
  // `data-crucible-text-done` when the LLM emits its final answer; that part
  // lands in the streaming assistant message's parts array BEFORE the stream
  // formally ends (smoothStream is still piping trailing tokens). This effect
  // watches for that part and flips isFinalizing=true so ChatPanel can show
  // "Finalizing…" without the user reaching for Stop. The onFinish handler
  // resets it; handleSubmit also resets at turn-start.
  useEffect(() => {
    if (status !== 'streaming') return;
    const lastMsg = aiMessages[aiMessages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return;
    const sawTextDone = (lastMsg.parts || []).some(p => p.type === 'data-crucible-text-done');
    if (sawTextDone) setIsFinalizing(true);
  }, [aiMessages, status]);

  // Bug E live tool tracker. Backend's per-tool data-crucible-tool-status
  // parts arrive on the in-flight assistant message; we want pills + per-
  // execution telemetry to fire as soon as each part lands, not wait until
  // onFinish (which is too late — the user wants to see "stride_query ran"
  // mid-turn, not after the entire stream finishes). The onFinish handler
  // remains as a post-stream safety net.
  useEffect(() => {
    if (status !== 'streaming') return;
    const lastMsg = aiMessages[aiMessages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return;
    const parts = (lastMsg.parts || []) as Array<{
      type?: string;
      data?: CrucibleToolStatusData;
    }>;
    for (const part of parts) {
      if (part.type !== 'data-crucible-tool-status') continue;
      const status = part.data;
      if (!status?.tool || status.tool === 'system') continue;
      const callId = status.toolCallId;
      if (!callId) continue;
      if (processedToolCallIdsRef.current.has(callId)) continue;
      processedToolCallIdsRef.current.add(callId);
      // See toExecutedToolStatus for why status.status, not a hardcoded
      // 'success', decides this (root cause of the Qualio-supplier
      // misdiagnosis, 2026-07-28).
      const mappedStatus = toExecutedToolStatus(status.status);
      executedToolsRef.current.push({
        name: status.tool,
        status: mappedStatus,
        timestamp: Date.now(),
        // Must mirror the onData push site above. Both consumers claim from
        // the same processedToolCallIdsRef, so exactly one of them records
        // any given call — a field added to only one is silently missing for
        // every tool the other claims. This effect runs while `streaming`, so
        // in practice it wins the race and claims nearly every data tool:
        // adding `input` above alone left tool_calls.args null for all 22
        // calls of a live stride_query turn (verified on the local rig).
        ...(status.input && typeof status.input === 'object' ? {
          input: status.input as Record<string, unknown>
        } : {}),
        ...(status.details ? {
          details: status.details
        } : {})
      });
      trackToolExecution(status.tool, {
        sessionId,
        success: mappedStatus !== 'error',
        durationMs: 0
      });
    }
  }, [aiMessages, status, sessionId]);
  return useMemo(() => ({
    messages,
    setMessages: setAiMessages,
    messageHistory,
    isGenerating,
    isFinalizing,
    modelInfo,
    turnActivity,
    turnStartedAt,
    streamingReasoning,
    streamingParts,
    messageToolsMap,
    // Map of messageId -> executed tools for display
    messageStatsMap,
    // Map of messageId -> usage + tool stats from stream_end
    messageThinkingMap,
    // Map of messageId -> seconds of actual reasoning
    messageTriageMap,
    // Map of messageId -> triage classification (placeholder)
    handleSubmit,
    handleStopGeneration,
    resetChat,
    error,
    reload: trackedRegenerate,
    injectContextToChat,
    /** Silently pinned context label/text (Norma viewer); null when none. */
    pinnedChatContext
  }), [messages, setAiMessages, messageHistory, isGenerating, isFinalizing, modelInfo, turnActivity, turnStartedAt, streamingReasoning, streamingParts, messageToolsMap, messageStatsMap, messageThinkingMap, messageTriageMap, handleSubmit, handleStopGeneration, resetChat, error, trackedRegenerate, injectContextToChat, pinnedChatContext]);
}